import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  User,
  Palette,
  Database,
  Upload,
  Settings,
  CheckCircle,
  Save,
  Eye,
  Youtube,
  Music2,
  Instagram,
  Facebook,
  ExternalLink,
  Zap,
  Plus,
  Pencil,
  Copy,
  DollarSign,
  Clock,
  Trash2,
  Image,
  HardDrive,
  Globe,
  Bookmark
} from 'lucide-react';
import { toast } from 'sonner';
import { PublishingMetadataDemo } from '@/components/creator-profile/PublishingMetadataDemo';

const API_URL = window.__BACKEND_URL;

/**
 * Creator Profile Page
 * 
 * PURPOSE: Configure creator-specific settings, brand kit, storage, and income tracking.
 * 
 * SECTIONS:
 * 1. Creator Context Header - Avatar, Name, ID, Status badges
 * 2. Brand Kit + Live Preview (mirrors App Profile)
 * 3. Defaults Panel (under preview)
 * 4. Storage & Platforms (mirrors App Profile)
 * 5. Automation & Manual Income
 * 6. Save Button (atomic)
 */
export default function CreatorProfile() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Creator data
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Storage & Platform status
  const [storageStatus, setStorageStatus] = useState(null);
  const [platformStatus, setPlatformStatus] = useState(null);
  const [platformConfig, setPlatformConfig] = useState({});
  
  // Active format: 'sf' or 'lf'
  const [activeFormat, setActiveFormat] = useState('sf');
  
  // Editable header fields
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  
  // Brand Kit state - Format-scoped (SF and LF)
  const [brandKit, setBrandKit] = useState({
    symbol_url: '',
    sf: {
      symbol_position: 'br',
      captions_enabled: true,
      caption_font: 'Inter',
      caption_primary_color: '#FFFFFF',
      caption_secondary_color: '#3A7BFF',
      caption_position: 'bottom',
      progress_bar_enabled: true,
      progress_bar_color: '#FF1493',
      progress_bar_position: 'top'
    },
    lf: {
      symbol_position: 'br',
      captions_enabled: true,
      caption_font: 'Inter',
      caption_primary_color: '#FFFFFF',
      caption_secondary_color: '#3A7BFF',
      caption_position: 'bottom',
      progress_bar_enabled: false,
      progress_bar_color: '#FF1493',
      progress_bar_position: 'top'
    }
  });
  
  // Defaults state
  const [defaultsSaved, setDefaultsSaved] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  
  // Manual Income
  const [incomeEntries, setIncomeEntries] = useState([]);
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [newIncome, setNewIncome] = useState({
    platform: 'YouTube',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Fetch creator profile
  const fetchCreatorProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/creator-profile`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCreator(data);
        setDisplayName(data.name || '');
        setAvatarUrl(data.avatar_url || '');
        
        // Load brand kit if exists
        if (data.brand_kit) {
          setBrandKit(prev => ({
            symbol_url: data.brand_kit.symbol_url || prev.symbol_url,
            sf: { ...prev.sf, ...data.brand_kit.sf },
            lf: { ...prev.lf, ...data.brand_kit.lf }
          }));
        }
        
        // Load income entries
        if (data.manual_income_entries) {
          setIncomeEntries(data.manual_income_entries);
        }
      }
    } catch (err) {
      console.error('Failed to fetch creator:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch storage status
  const fetchStorageStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/storage/status`, { credentials: 'include' });
      if (res.ok) {
        setStorageStatus(await res.json());
      }
    } catch (err) {}
  }, []);
  
  // Fetch platform connections - MUST use owner_type=creator and creator profile ID
  const fetchPlatformStatus = useCallback(async () => {
    if (!creator?.id) return;
    
    try {
      const [statusRes, configRes] = await Promise.all([
        fetch(`${API_URL}/api/social/status?owner_type=creator&owner_id=${creator.id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/social/config-status`, { credentials: 'include' })
      ]);
      if (statusRes.ok) {
        setPlatformStatus(await statusRes.json());
      }
      if (configRes.ok) {
        setPlatformConfig(await configRes.json());
      }
    } catch (err) {
      console.error('Failed to fetch platform status');
    }
  }, [creator?.id]);
  
  useEffect(() => {
    fetchCreatorProfile();
    fetchStorageStatus();
  }, [fetchCreatorProfile, fetchStorageStatus]);
  
  useEffect(() => {
    if (creator?.id) {
      fetchPlatformStatus();
    }
  }, [creator?.id, fetchPlatformStatus]);
  
  // Handle OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'connected') {
      toast.success('Platform connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      fetchPlatformStatus();
    } else if (params.get('error')) {
      toast.error(`Connection failed: ${params.get('error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchPlatformStatus]);
  
  // Handle brand kit changes (format-scoped)
  const handleBrandKitChange = (field, value) => {
    if (field === 'symbol_url') {
      setBrandKit(prev => ({ ...prev, symbol_url: value }));
    } else {
      setBrandKit(prev => ({
        ...prev,
        [activeFormat]: { ...prev[activeFormat], [field]: value }
      }));
    }
    setHasChanges(true);
  };
  
  // Handle symbol upload
  const handleSymbolUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['image/png', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload PNG or SVG only');
      return;
    }
    
    const url = URL.createObjectURL(file);
    handleBrandKitChange('symbol_url', url);
    toast.success('Symbol uploaded');
  };
  
  // Connect platform - CRITICAL: must have valid creator profile
  const handleConnectPlatform = (platform) => {
    if (!creator?.id) {
      toast.error('No creator profile found. Please refresh the page.');
      return;
    }
    window.location.href = `${API_URL}/api/social/connect/${platform}?owner_type=creator&owner_id=${creator.id}`;
  };
  
  // Connect Dropbox
  const handleConnectDropbox = () => {
    window.location.href = `${API_URL}/api/storage/dropbox/connect`;
  };
  
  // Copy creator ID
  const handleCopyId = () => {
    if (creator?.id) {
      navigator.clipboard.writeText(creator.id);
      toast.success('Creator ID copied');
    }
  };
  
  // Save all changes atomically
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: displayName,
        avatar_url: avatarUrl,
        brand_kit: brandKit,
        manual_income_entries: incomeEntries
      };
      
      const res = await fetch(`${API_URL}/api/creator-profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Creator Profile saved');
        setHasChanges(false);
        fetchCreatorProfile();
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  // Save defaults
  const handleSaveDefaults = async () => {
    setSavingDefaults(true);
    try {
      const res = await fetch(`${API_URL}/api/creator-profile/defaults`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_kit: brandKit })
      });
      
      if (res.ok) {
        toast.success('Defaults saved! Will apply to future sessions.');
        setDefaultsSaved(true);
      } else {
        toast.error('Failed to save defaults');
      }
    } catch (error) {
      toast.error('Failed to save defaults');
    } finally {
      setSavingDefaults(false);
    }
  };
  
  // Add income entry
  const handleAddIncome = () => {
    if (!newIncome.amount || parseFloat(newIncome.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    const entry = {
      id: Date.now().toString(),
      platform: newIncome.platform,
      amount: parseFloat(newIncome.amount),
      date: newIncome.date,
      notes: newIncome.notes || ''
    };
    
    setIncomeEntries(prev => [...prev, entry]);
    setNewIncome({ platform: 'YouTube', amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
    setShowIncomeForm(false);
    setHasChanges(true);
    toast.success('Income entry added');
  };
  
  // Delete income entry
  const handleDeleteIncome = (id) => {
    setIncomeEntries(prev => prev.filter(e => e.id !== id));
    setHasChanges(true);
  };
  
  // Current format config
  const currentConfig = brandKit[activeFormat];
  const connectedCount = platformStatus?.connected_count || 0;
  const connections = platformStatus?.connections || {};
  
  // Loading state
  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="creator-profile-page">
      {/* ========== SECTION 1: CREATOR CONTEXT HEADER ========== */}
      <div className="mb-8 p-6 rounded-xl bg-[#1C1F26] border border-white/10">
        <div className="flex items-start gap-6">
          {/* Editable Avatar */}
          <div 
            className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center flex-shrink-0 relative group cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover rounded-full" />
            ) : (
              <User className="h-10 w-10 text-white" />
            )}
            <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <Upload className="h-5 w-5 text-white" />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setAvatarUrl(URL.createObjectURL(file));
                  setHasChanges(true);
                }
              }}
              className="hidden"
            />
          </div>
          
          {/* Editable Name & ID */}
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Input
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setHasChanges(true); }}
                placeholder="Creator Display Name"
                className="text-2xl font-bold bg-transparent border-dashed border-white/20 text-white h-auto py-1 px-2 focus:border-pink-500 max-w-md"
                data-testid="creator-name-input"
              />
              <Pencil className="h-4 w-4 text-[#8A93A6]" />
            </div>
            <div className="flex items-center gap-2 text-sm text-[#8A93A6]">
              <span>ID:</span>
              <code className="bg-black/40 px-2 py-0.5 rounded text-xs font-mono">{creator?.id || 'N/A'}</code>
              <Button variant="ghost" size="sm" onClick={handleCopyId} className="h-6 w-6 p-0">
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
          
          {/* Status Badges */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={storageStatus?.dropbox?.is_connected ? 'border-green-500/30 text-green-400' : 'border-yellow-500/30 text-yellow-400'}>
              {storageStatus?.dropbox?.is_connected ? (
                <><HardDrive className="h-3 w-3 mr-1" />Dropbox Connected</>
              ) : (
                <><Database className="h-3 w-3 mr-1" />Temp Storage</>
              )}
            </Badge>
            <Badge variant="outline" className="border-blue-500/30 text-blue-400">
              <Globe className="h-3 w-3 mr-1" />
              {connectedCount} Platforms
            </Badge>
            <Badge variant="outline" className={creator?.monetization_eligible ? 'border-green-500/30 text-green-400' : 'border-gray-500/30 text-gray-400'}>
              <DollarSign className="h-3 w-3 mr-1" />
              {creator?.monetization_eligible ? 'Monetization Ready' : 'Not Eligible'}
            </Badge>
            <Badge variant="outline" className={creator?.autopilot_eligible ? 'border-purple-500/30 text-purple-400' : 'border-gray-500/30 text-gray-400'}>
              <Zap className="h-3 w-3 mr-1" />
              {creator?.autopilot_eligible ? 'Autopilot Ready' : 'Autopilot N/A'}
            </Badge>
          </div>
        </div>
      </div>
      
      {/* ========== SECTION 2: BRAND KIT & PREVIEW ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* LEFT: Brand Kit */}
        <Card className="glass h-full">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Palette className="h-5 w-5 text-pink-500" />
              Brand Kit
            </CardTitle>
            <CardDescription>Configure your content branding</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Format Toggle */}
            <div className="flex items-center justify-center p-1 bg-black/40 rounded-lg">
              <button
                onClick={() => setActiveFormat('sf')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  activeFormat === 'sf' ? 'bg-pink-500 text-white' : 'text-[#8A93A6] hover:text-white'
                }`}
                data-testid="format-toggle-sf"
              >
                Short Form
              </button>
              <button
                onClick={() => setActiveFormat('lf')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  activeFormat === 'lf' ? 'bg-pink-500 text-white' : 'text-[#8A93A6] hover:text-white'
                }`}
                data-testid="format-toggle-lf"
              >
                Long Form
              </button>
            </div>
            
            {/* Symbol */}
            <div className="space-y-3">
              <Label className="text-white font-medium">Symbol</Label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden">
                  {brandKit.symbol_url ? (
                    <img src={brandKit.symbol_url} alt="Symbol" className="w-full h-full object-contain" />
                  ) : (
                    <Image className="h-6 w-6 text-[#8A93A6]" />
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => document.getElementById('symbol-upload')?.click()}
                  className="border-white/10"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {brandKit.symbol_url ? 'Replace' : 'Upload'}
                </Button>
                <input
                  id="symbol-upload"
                  type="file"
                  accept=".png,.svg"
                  onChange={handleSymbolUpload}
                  className="hidden"
                />
              </div>
              {/* Position */}
              <div>
                <Label className="text-xs text-[#8A93A6]">Position ({activeFormat === 'sf' ? 'Short Form' : 'Long Form'})</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {[{ value: 'tl', label: 'TL' }, { value: 'tr', label: 'TR' }, { value: 'bl', label: 'BL' }, { value: 'br', label: 'BR' }].map((pos) => (
                    <button
                      key={pos.value}
                      onClick={() => handleBrandKitChange('symbol_position', pos.value)}
                      className={`py-2 rounded text-xs font-medium transition-all ${
                        currentConfig.symbol_position === pos.value
                          ? 'bg-pink-500 text-white'
                          : 'bg-black/40 text-[#8A93A6] hover:text-white border border-white/10'
                      }`}
                    >
                      {pos.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Captions */}
            <div className="space-y-3 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <Label className="text-white font-medium">Captions</Label>
                <Switch
                  checked={currentConfig.captions_enabled}
                  onCheckedChange={(v) => handleBrandKitChange('captions_enabled', v)}
                />
              </div>
              {currentConfig.captions_enabled && (
                <div className="space-y-3 pl-4 border-l-2 border-pink-500/30">
                  <div>
                    <Label className="text-xs text-[#8A93A6]">Font</Label>
                    <Select value={currentConfig.caption_font} onValueChange={(v) => handleBrandKitChange('caption_font', v)}>
                      <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Inter">Inter</SelectItem>
                        <SelectItem value="Arial">Arial</SelectItem>
                        <SelectItem value="Roboto">Roboto</SelectItem>
                        <SelectItem value="Poppins">Poppins</SelectItem>
                        <SelectItem value="Montserrat">Montserrat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#8A93A6]">Primary Color</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input type="color" value={currentConfig.caption_primary_color} onChange={(e) => handleBrandKitChange('caption_primary_color', e.target.value)} className="h-8 w-12 p-1 bg-transparent border-white/10" />
                        <Input value={currentConfig.caption_primary_color} onChange={(e) => handleBrandKitChange('caption_primary_color', e.target.value)} className="bg-black/40 border-white/10 text-white text-xs flex-1" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-[#8A93A6]">Secondary Color</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input type="color" value={currentConfig.caption_secondary_color} onChange={(e) => handleBrandKitChange('caption_secondary_color', e.target.value)} className="h-8 w-12 p-1 bg-transparent border-white/10" />
                        <Input value={currentConfig.caption_secondary_color} onChange={(e) => handleBrandKitChange('caption_secondary_color', e.target.value)} className="bg-black/40 border-white/10 text-white text-xs flex-1" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A93A6]">Position</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      {['top', 'center', 'bottom'].map((pos) => (
                        <button
                          key={pos}
                          onClick={() => handleBrandKitChange('caption_position', pos)}
                          className={`py-2 rounded text-xs font-medium capitalize transition-all ${
                            currentConfig.caption_position === pos
                              ? 'bg-pink-500 text-white'
                              : 'bg-black/40 text-[#8A93A6] hover:text-white border border-white/10'
                          }`}
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Progress Bar */}
            <div className="space-y-3 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <Label className="text-white font-medium">Progress Bar</Label>
                <Switch
                  checked={currentConfig.progress_bar_enabled}
                  onCheckedChange={(v) => handleBrandKitChange('progress_bar_enabled', v)}
                />
              </div>
              {currentConfig.progress_bar_enabled && (
                <div className="pl-4 border-l-2 border-pink-500/30 space-y-3">
                  <div>
                    <Label className="text-xs text-[#8A93A6]">Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input type="color" value={currentConfig.progress_bar_color} onChange={(e) => handleBrandKitChange('progress_bar_color', e.target.value)} className="h-8 w-12 p-1 bg-transparent border-white/10" />
                      <Input value={currentConfig.progress_bar_color} onChange={(e) => handleBrandKitChange('progress_bar_color', e.target.value)} className="bg-black/40 border-white/10 text-white text-xs flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A93A6]">Position</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      {['top', 'bottom'].map((pos) => (
                        <button
                          key={pos}
                          onClick={() => handleBrandKitChange('progress_bar_position', pos)}
                          className={`py-2 rounded text-xs font-medium capitalize transition-all ${
                            currentConfig.progress_bar_position === pos
                              ? 'bg-pink-500 text-white'
                              : 'bg-black/40 text-[#8A93A6] hover:text-white border border-white/10'
                          }`}
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* RIGHT: Live Preview (with Defaults inside) */}
        <Card className="glass h-full">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Eye className="h-5 w-5 text-green-500" />
              Live Preview
            </CardTitle>
            <CardDescription>
              {activeFormat === 'sf' ? 'Short-Form Preview' : 'Long-Form Preview'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Preview Canvas */}
            <div className="relative rounded-lg bg-black border border-white/10 overflow-hidden h-[280px] flex items-center justify-center">
              <div className={`relative bg-gradient-to-br from-gray-800 to-gray-900 ${
                activeFormat === 'sf' ? 'h-full aspect-[9/16]' : 'w-full aspect-video'
              }`} style={{ maxHeight: '100%', maxWidth: '100%' }}>
                {/* Format Label */}
                <div className="absolute top-2 left-2 z-10">
                  <Badge className="bg-black/60 text-white text-xs">
                    {activeFormat === 'sf' ? 'Short-Form' : 'Long-Form'}
                  </Badge>
                </div>
                
                {/* Symbol */}
                {brandKit.symbol_url && (
                  <div className={`absolute w-8 h-8 p-1 ${
                    currentConfig.symbol_position === 'tl' ? 'top-2 left-2' :
                    currentConfig.symbol_position === 'tr' ? 'top-2 right-2' :
                    currentConfig.symbol_position === 'bl' ? 'bottom-2 left-2' :
                    'bottom-2 right-2'
                  }`}>
                    <img src={brandKit.symbol_url} alt="Symbol" className="w-full h-full object-contain" />
                  </div>
                )}
                
                {/* Progress Bar */}
                {currentConfig.progress_bar_enabled && (
                  <div className={`absolute left-0 right-0 h-1 ${
                    currentConfig.progress_bar_position === 'bottom' ? 'bottom-0' : 'top-0'
                  }`}>
                    <div className="h-full w-2/3" style={{ backgroundColor: currentConfig.progress_bar_color }} />
                  </div>
                )}
                
                {/* Caption */}
                {currentConfig.captions_enabled && (
                  <div className={`absolute left-0 right-0 px-2 text-center ${
                    currentConfig.caption_position === 'top' ? 'top-8' :
                    currentConfig.caption_position === 'center' ? 'top-1/2 -translate-y-1/2' :
                    'bottom-8'
                  }`}>
                    <p className="text-xs font-semibold drop-shadow-lg" style={{ fontFamily: currentConfig.caption_font, color: currentConfig.caption_primary_color }}>
                      Sample <span style={{ color: currentConfig.caption_secondary_color }}>Caption</span>
                    </p>
                  </div>
                )}
                
                {/* Center placeholder */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <Eye className="h-6 w-6 text-white/30 mx-auto mb-1" />
                    <p className="text-white/40 text-xs">Preview</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Defaults (inside same card) */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <Bookmark className="h-4 w-4 text-cyan-500" />
                <span className="text-white font-medium text-sm">Defaults</span>
              </div>
              <div className="p-3 rounded-lg bg-black/40 border border-white/10 mb-3">
                <p className="text-xs text-[#8A93A6]">
                  <strong className="text-white">Current:</strong> {defaultsSaved || creator?.has_defaults ? 'Custom defaults saved' : 'Using system defaults'}
                </p>
                <p className="text-xs text-[#8A93A6] mt-1">Applies to future sessions only.</p>
              </div>
              <Button 
                onClick={handleSaveDefaults}
                disabled={savingDefaults}
                size="sm"
                className="w-full bg-cyan-500 hover:bg-cyan-600"
              >
                <Bookmark className="h-3 w-3 mr-2" />
                {savingDefaults ? 'Saving...' : 'Set current design as default'}
              </Button>
            </div>

            {/* Publishing Metadata + AI Metadata Setup — UI/simulated UX only, see PublishingMetadataDemo.jsx */}
            <PublishingMetadataDemo />
          </CardContent>
        </Card>
      </div>
      
      {/* ========== SECTION 3: STORAGE & PLATFORMS ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* LEFT: Storage */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Database className="h-5 w-5 text-purple-500" />
              Storage
              {storageStatus?.dropbox?.is_connected && (
                <Badge variant="outline" className="ml-2 border-green-500/30 text-green-400 text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              )}
            </CardTitle>
            <CardDescription>Your content storage location</CardDescription>
          </CardHeader>
          <CardContent>
            {storageStatus?.dropbox?.is_connected ? (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-white text-sm font-medium">Dropbox Connected</p>
                  <p className="text-xs text-[#8A93A6] font-mono mt-1">{storageStatus.dropbox.root_path || '/Consistency'}/</p>
                </div>
                <Button variant="outline" className="w-full border-white/10" onClick={() => navigate('/settings/storage')}>
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Storage
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-center">
                  <Database className="h-6 w-6 text-yellow-500 mx-auto mb-1" />
                  <p className="text-yellow-400 text-xs">Using temporary storage</p>
                </div>
                <Button variant="outline" className="w-full border-white/10" onClick={handleConnectDropbox}>
                  Connect Dropbox
                </Button>
                <Button variant="outline" className="w-full border-white/10" onClick={() => navigate('/settings/storage')}>
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Storage
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* RIGHT: Platforms */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">Connected Platforms</CardTitle>
              <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 text-xs">
                {connectedCount} connected
              </Badge>
            </div>
            <CardDescription>Where your content gets published</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {!creator?.id ? (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-center">
                <p className="text-red-400 text-sm">No creator profile found. Cannot connect platforms.</p>
              </div>
            ) : (
              <>
                <PlatformRow platform="youtube" name="YouTube" icon={Youtube} color="text-red-500" bgColor="bg-red-500/10" borderColor="border-red-500/30" connection={connections.youtube} isConfigured={platformConfig.youtube} onConnect={() => handleConnectPlatform('youtube')} onManage={() => navigate('/social-accounts')} />
                <PlatformRow platform="tiktok" name="TikTok" icon={Music2} color="text-pink-500" bgColor="bg-pink-500/10" borderColor="border-pink-500/30" connection={connections.tiktok} isConfigured={platformConfig.tiktok} onConnect={() => handleConnectPlatform('tiktok')} onManage={() => navigate('/social-accounts')} />
                <PlatformRow platform="instagram" name="Instagram" icon={Instagram} color="text-purple-500" bgColor="bg-purple-500/10" borderColor="border-purple-500/30" connection={connections.instagram} isConfigured={platformConfig.instagram} onConnect={() => handleConnectPlatform('instagram')} onManage={() => navigate('/social-accounts')} />
                <PlatformRow platform="facebook" name="Facebook" icon={Facebook} color="text-blue-500" bgColor="bg-blue-500/10" borderColor="border-blue-500/30" connection={connections.facebook} isConfigured={platformConfig.facebook} onConnect={() => handleConnectPlatform('facebook')} onManage={() => navigate('/social-accounts')} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* ========== SECTION 4: AUTOMATION & MANUAL INCOME ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* LEFT: Eligibility Panels */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="h-5 w-5 text-yellow-500" />
              Automation & Eligibility
            </CardTitle>
            <CardDescription>Check your eligibility status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Autopilot Eligibility */}
            <div 
              onClick={() => navigate('/app/create/autopilot')}
              className="p-4 rounded-lg bg-black/40 border border-white/10 hover:bg-white/5 cursor-pointer transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Zap className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Autopilot Eligibility</p>
                    <p className="text-xs text-[#8A93A6]">Auto-post your content</p>
                  </div>
                </div>
                <Badge variant="outline" className={creator?.autopilot_eligible ? 'border-green-500/30 text-green-400' : 'border-gray-500/30 text-gray-400'}>
                  {creator?.autopilot_eligible ? 'Eligible' : 'Check Status'}
                </Badge>
              </div>
            </div>
            
            {/* Monetization Eligibility */}
            <div 
              onClick={() => navigate('/app/analyze/monetization-insights')}
              className="p-4 rounded-lg bg-black/40 border border-white/10 hover:bg-white/5 cursor-pointer transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <DollarSign className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Monetization Insights</p>
                    <p className="text-xs text-[#8A93A6]">Revenue estimates & eligibility</p>
                  </div>
                </div>
                <Badge variant="outline" className={creator?.monetization_eligible ? 'border-green-500/30 text-green-400' : 'border-gray-500/30 text-gray-400'}>
                  {creator?.monetization_eligible ? 'Eligible' : 'Check Status'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* RIGHT: Manual Income */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="h-5 w-5 text-green-500" />
                Manual Income
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => setShowIncomeForm(true)} className="border-white/10">
                <Plus className="h-4 w-4 mr-1" />
                Add Entry
              </Button>
            </div>
            <CardDescription>Track income from all platforms</CardDescription>
          </CardHeader>
          <CardContent>
            {showIncomeForm && (
              <div className="p-4 rounded-lg bg-black/40 border border-white/10 mb-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Platform</Label>
                    <Select value={newIncome.platform} onValueChange={(v) => setNewIncome(p => ({ ...p, platform: v }))}>
                      <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="YouTube">YouTube</SelectItem>
                        <SelectItem value="TikTok">TikTok</SelectItem>
                        <SelectItem value="Instagram">Instagram</SelectItem>
                        <SelectItem value="Facebook">Facebook</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Amount ($)</Label>
                    <Input 
                      type="number" 
                      value={newIncome.amount} 
                      onChange={(e) => setNewIncome(p => ({ ...p, amount: e.target.value }))}
                      placeholder="0.00"
                      className="bg-black/40 border-white/10 text-white mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input 
                    type="date" 
                    value={newIncome.date} 
                    onChange={(e) => setNewIncome(p => ({ ...p, date: e.target.value }))}
                    className="bg-black/40 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Notes (optional)</Label>
                  <Input 
                    value={newIncome.notes} 
                    onChange={(e) => setNewIncome(p => ({ ...p, notes: e.target.value }))}
                    placeholder="e.g., Monthly AdSense"
                    className="bg-black/40 border-white/10 text-white mt-1"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddIncome} className="bg-green-500 hover:bg-green-600">
                    Add
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowIncomeForm(false)} className="border-white/10">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            {incomeEntries.length === 0 ? (
              <div className="p-4 rounded-lg bg-black/40 border border-white/10 text-center">
                <DollarSign className="h-8 w-8 text-[#8A93A6] mx-auto mb-2" />
                <p className="text-[#8A93A6] text-sm">No income entries yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {incomeEntries.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-white/10">
                    <div>
                      <p className="text-white font-medium">${entry.amount.toFixed(2)}</p>
                      <p className="text-xs text-[#8A93A6]">{entry.platform} • {new Date(entry.date).toLocaleDateString()}</p>
                      {entry.notes && <p className="text-xs text-[#8A93A6] mt-1">{entry.notes}</p>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteIncome(entry.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* ========== SAVE BUTTON ========== */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`px-8 ${hasChanges ? 'bg-pink-500 hover:bg-pink-600' : 'bg-gray-600 cursor-not-allowed'}`}
          data-testid="save-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

/** Platform Row Component */
function PlatformRow({ platform, name, icon: Icon, color, bgColor, borderColor, connection, isConfigured = true, onConnect, onManage }) {
  const isConnected = connection?.is_connected;
  
  return (
    <div className={`flex items-center justify-between p-2.5 rounded-lg border ${isConnected ? borderColor : 'border-gray-700'} ${isConnected ? bgColor : 'bg-gray-800/50'}`}>
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded ${bgColor}`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <span className="text-white text-sm font-medium">{name}</span>
        {isConnected && (
          <Badge className="bg-green-500/20 text-green-400 text-xs py-0">
            <CheckCircle className="w-3 h-3 mr-1" />
            Connected
          </Badge>
        )}
        {!isConnected && !isConfigured && (
          <span className="text-xs text-yellow-500">Not configured</span>
        )}
      </div>
      {isConnected ? (
        <Button variant="outline" size="sm" onClick={onManage} className={`${borderColor} ${color} text-xs h-7`}>
          Manage
        </Button>
      ) : (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onConnect} 
          disabled={!isConfigured}
          className={isConfigured ? `${borderColor} ${color} text-xs h-7` : 'border-gray-600 text-gray-500 text-xs h-7 cursor-not-allowed'}
        >
          <ExternalLink className="w-3 h-3 mr-1" />
          Connect
        </Button>
      )}
    </div>
  );
}
